import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import Navbar from './components/Navbar';
import FloatingWhatsapp from './components/FloatingWhatsapp';
import HeroV2 from './components/homev2/HeroV2';
import defaultContent from './data/defaultContent.json';
import { initGlobalLeadTracking } from './utils/analytics';
import './App.css';

// Lazy-loaded routes for code splitting and instant initial page load
const HomeV2 = lazy(() => import('./pages/homev2/HomeV2'));
const About = lazy(() => import('./pages/About'));
const AgenticAI = lazy(() => import('./pages/AgenticAI'));
const ESG = lazy(() => import('./pages/ESG'));
const SoftwareDataAI = lazy(() => import('./pages/SoftwareDataAI'));
const CloudService = lazy(() => import('./pages/CloudService'));
const IATFAuditing = lazy(() => import('./pages/IATFAuditing'));
const ErpAI = lazy(() => import('./pages/ErpAI'));
const StaffAugmentation = lazy(() => import('./pages/StaffAugmentation'));
const MicrosoftPartnership = lazy(() => import('./pages/MicrosoftPartnership'));
const Contact = lazy(() => import('./pages/Contact'));
const Careers = lazy(() => import('./pages/Careers'));
const Blogs = lazy(() => import('./pages/Blogs'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const SiteIndex = lazy(() => import('./pages/SiteIndex'));
const Admin = lazy(() => import('./pages/Admin'));
const NotFound = lazy(() => import('./pages/NotFound'));

// While the lazy home chunk loads, render the same hero that is already in the pre-built HTML so the
// first screen never blanks out into a spinner (React replaces the static copy on its first commit).
function HomeFallback() {
  const home = defaultContent.home || {};
  return (
    <div className="v2-home-page">
      <HeroV2 content={home.hero || {}} whatsappLink={home.cta && home.cta.whatsappLink} />
    </div>
  );
}

function PageLoadingFallback() {
  return (
    <div className="v2-route-suspense-fallback" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="v2-spinner"></div>
    </div>
  );
}

function BlogSlugRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/blog/${slug}`} replace />;
}

function BlogCategoryRedirect() {
  const { category, page } = useParams();
  if (page) {
    return <Navigate to={`/blogs/category/${category}/page/${page}`} replace />;
  }
  return <Navigate to={`/blogs/category/${category}`} replace />;
}

function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace(/^#/, '');
      const scrollToElement = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          return true;
        }
        return false;
      };

      if (!scrollToElement()) {
        const timers = [
          setTimeout(scrollToElement, 150),
          setTimeout(scrollToElement, 450),
          setTimeout(scrollToElement, 850),
          setTimeout(scrollToElement, 1400)
        ];
        return () => timers.forEach(clearTimeout);
      }
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [location.pathname, location.hash]);
  return null;
}

function AnimatedRoutes() {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState('');

  useEffect(() => {
    if (location.pathname !== displayLocation.pathname) {
      setTransitionStage('fadeOut');
    }
  }, [location, displayLocation]);

  const handleAnimationEnd = () => {
    if (transitionStage === 'fadeOut') {
      setDisplayLocation(location);
      setTransitionStage('fadeIn');
    } else if (transitionStage === 'fadeIn') {
      setTransitionStage('');
    }
  };

  return (
    <div
      className={`page-transition ${transitionStage}`}
      onAnimationEnd={handleAnimationEnd}
      key={displayLocation.pathname}
    >
      <Suspense fallback={displayLocation.pathname === '/' ? <HomeFallback /> : <PageLoadingFallback />}>
        <Routes location={displayLocation}>
          <Route path="/admin" element={<Admin />} />
          <Route path="/" element={<HomeV2 />} />
          <Route path="/about" element={<About />} />
          <Route path="/agentic-ai" element={<AgenticAI />} />
          <Route path="/esg" element={<ESG />} />
          <Route path="/digital-reach" element={<Navigate to="/" replace />} />
          <Route path="/digital-transformation" element={<Navigate to="/" replace />} />
          <Route path="/software-data-ai" element={<SoftwareDataAI />} />
          <Route path="/cloud-service" element={<CloudService />} />
          <Route path="/iatf-auditing" element={<IATFAuditing />} />
          <Route path="/erp-ai" element={<ErpAI />} />
          <Route path="/staff-augmentation" element={<StaffAugmentation />} />
          <Route path="/microsoft-partnership" element={<MicrosoftPartnership />} />
          <Route path="/partnership" element={<MicrosoftPartnership />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/troy" element={<Contact />} />
          <Route path="/toronto" element={<Contact />} />
          <Route path="/locations/troy" element={<Navigate to="/troy" replace />} />
          <Route path="/locations/toronto" element={<Navigate to="/toronto" replace />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/career" element={<Navigate to="/careers" replace />} />
          <Route path="/jobs" element={<Navigate to="/careers" replace />} />
          <Route path="/blog" element={<Navigate to="/blogs" replace />} />
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/blogs/page/:page" element={<Blogs />} />
          <Route path="/blogs/category/:category" element={<Blogs />} />
          <Route path="/blogs/category/:category/page/:page" element={<Blogs />} />
          <Route path="/blog/category/:category" element={<BlogCategoryRedirect />} />
          <Route path="/blog/category/:category/page/:page" element={<BlogCategoryRedirect />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/blogs/:slug" element={<BlogSlugRedirect />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
          <Route path="/sitemap" element={<SiteIndex />} />
          <Route path="/site-index" element={<SiteIndex />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Uncaught component error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div style={{ minHeight: '100vh', background: '#060813', color: '#ffffff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center', fontFamily: 'system-ui, sans-serif' }}>
          <img src="/images/02.png" alt="Venus Global Technology" style={{ height: '48px', marginBottom: '1.5rem' }} />
          <h1 style={{ fontSize: '1.8rem', fontWeight: 700, marginBottom: '0.75rem' }}>Experience Temporary Interruption</h1>
          <p style={{ color: '#94a3b8', maxWidth: '500px', lineHeight: 1.6, marginBottom: '1.5rem' }}>
            We encountered a slight display glitch while rendering this section. Our automated recovery is standing by.
          </p>
          <button
            onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', padding: '12px 28px', borderRadius: '8px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer' }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const location = useLocation();
  const isAdmin = location.pathname === '/admin' || location.pathname.startsWith('/admin');

  useEffect(() => {
    initGlobalLeadTracking();
  }, []);

  return (
    <div className="App">
      <ScrollToTop />
      {!isAdmin && (
        <ErrorBoundary fallback={null}>
          <Navbar />
        </ErrorBoundary>
      )}
      <AnimatedRoutes />
      {!isAdmin && <FloatingWhatsapp phoneNumber="12482751077" />}
    </div>
  );
}

function AppWrapper() {
  return (
    <Router>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </Router>
  );
}

export default AppWrapper;

